import uniq from "lodash.uniq";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ArrowRight from "@/assets/icons/arrow-right.svg?react";
import Medal1st from "@/assets/icons/medal-1st.svg?react";
import Medal2nd from "@/assets/icons/medal-2nd.svg?react";
import Medal3rd from "@/assets/icons/medal-3rd.svg?react";
import AnalysisLink from "@/components/ui/bi/BITable/AnalysisLink";
import { routes } from "@/configs/router.config/routes";
import { useAnalyses } from "@/hooks/useAnalyses";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { cn } from "@/lib/utils";
import { DisplayGroup } from "@/models/displayGroup";
import { VocCountData } from "@/models/vocCount";

interface CategoryRankingItem {
  categoryPath: string;
  category: string;
  count: number;
  percentage: number;
}

interface Props {
  vocCountData: VocCountData;
  selectedCategoryPath: string;
  displayGroup: DisplayGroup;
  currentStats: number[];
  isYearlyView?: boolean;
}

const MedalIcon = ({ rank }: { rank: number; }) => {
  if (rank === 0) return <Medal1st className="size-6 shrink-0" />;
  if (rank === 1) return <Medal2nd className="size-6 shrink-0" />;
  if (rank === 2) return <Medal3rd className="size-6 shrink-0" />;
  return null;
};

const KumiaiCategoryRanking: React.FC<Props> = ({
  vocCountData,
  displayGroup,
}) => {
  const navigate = useNavigate();
  const { latestAnalysis } = useAnalyses();
  const { sessionCurrentWorkspaceId, categoryMaxLevel } = useWorkspaces();

  // カテゴリ別のランキングデータを作成
  const rankingData = useMemo<CategoryRankingItem[]>(() => {
    const categories = uniq(vocCountData.counts.map((vc) => vc.getLayerName(1)));

    const items = categories.map((category) => {
      const childVocCountData = vocCountData.filterByCategoryName(category);
      const latestCount = childVocCountData.stats.at(-1) ?? 0;

      return {
        categoryPath: childVocCountData.currentFilteringPath,
        category,
        count: latestCount,
        percentage: vocCountData.thisMonthCount
          ? Math.round((latestCount / vocCountData.thisMonthCount) * 1000) / 10
          : 0,
      };
    });

    // 回答数が多い順にソート、上位3つを取得
    return items.toSorted((a, b) => b.count - a.count).slice(0, 3);
  }, [vocCountData]);

  const totalCount = vocCountData.thisMonthCount ?? 0;

  const handleAnalysisClick = (categoryPath: string) => {
    const base = routes.analysis(sessionCurrentWorkspaceId)(displayGroup.id);
    if (!latestAnalysis) {
      navigate(base);
      return;
    }

    // 指定されたカテゴリパス配下の最下層カテゴリを取得（最初に見つかった1つ）
    const deepestCategoryPath = categoryPath.startsWith("/")
      ? vocCountData.counts
        .filter((vc) => vc.categoryPath.startsWith(categoryPath))
        .filter((vc) => vc.layers.length === categoryMaxLevel - 1)
        .map((vc) => vc.categoryPath)
        .at(0) ?? categoryPath
      : categoryPath;

    const path = `${base}?analysis=${latestAnalysis.id}&category=${encodeURIComponent(deepestCategoryPath)}`;
    navigate(path);
  };

  return (
    <div className="flex items-center ml-[6px] w-full">
      {/* 矢印アイコン */}
      <div className="flex h-full w-5 mr-4 items-center justify-center">
        <ArrowRight className="h-5 w-[60px] rotate-90" style={{ fill: "#E8EDFB" }} />
      </div>

      {/* ランキング表示 */}
      {/* py-4 w-[550px] border border-gray-200 rounded-[6px] */}
      <div className="flex h-[200px] w-full flex-col gap-2 rounded-[10px] border border-gray-200 bg-white p-4">
        <p className="text-center text-sm font-bold text-black">
          カテゴリ別任意項目の回答数ランキング
        </p>

        <div className="flex flex-col gap-4">
          {rankingData.map((item, index) => {
            // プログレスバーの幅（最大300px）
            const barWidth = item.percentage > 0 ? Math.round((item.percentage / 100) * 300) : 0;

            return (
              <div key={item.categoryPath} className="flex items-center gap-2">
                {/* メダルアイコン */}
                <MedalIcon rank={index} />

                {/* カテゴリ情報 */}
                <div className="flex flex-col gap-0.5">
                  {/* カテゴリ名 */}
                  <p className="text-sm font-medium leading-none text-[#333333]" data-testid="category-name">
                    {item.category}
                  </p>

                  {/* プログレスバーと数値 */}
                  <div className="flex items-center gap-4 h-[14px]">
                    {/* プログレスバー */}
                    <div className="relative h-3 w-[300px] overflow-hidden rounded-lg bg-[#e8edfb]">
                      <div
                        className={cn(
                          "absolute left-0 top-0 h-full rounded-lg bg-gradient-to-r from-[#577cfa] to-[#5399f0]",
                        )}
                        style={{ width: `${barWidth}px` }}
                      />
                    </div>

                    {/* 数値表示 */}
                    <div className="flex items-center gap-2.5 text-right leading-none w-[146px]">
                      <p className="w-20 text-[#333333]">
                        <span className="font-['Montserrat'] text-sm font-bold text-[#5871fe]">
                          {item.count}
                        </span>
                        <span className="font-['Montserrat'] text-xs font-medium">/ {totalCount}</span>
                        <span className="text-xs font-medium">件</span>
                      </p>
                      <p className="font-['Montserrat'] text-sm font-bold text-[#5871fe] text-nowrap">
                        {item.percentage}％
                      </p>
                    </div>
                  </div>
                </div>

                {/* 分析ボタン */}
                {displayGroup.id && ( // displayGroup.idが存在しないのは全体概要
                  <AnalysisLink
                    className="ml-0 translate-y-0.5 self-end"
                    hasLink={true}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAnalysisClick(item.categoryPath);
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default KumiaiCategoryRanking;
